"use client";

import React, { useState, useEffect } from "react";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { ArrowLeft, FileCheck2, Plus, Trash2, Upload, MessageSquare, Save, Loader2 } from "lucide-react";
import { ReviewComment } from "../../shared/ReviewComment";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { getFY_YEAR } from "@/utils/sharepoint-env";

interface LeaseAssessmentProps {
  onBack?: () => void;
  setActiveSection?: (section: string) => void;
}

interface LeaseDetails {
  id: string;
  name: string;
  leaseType: string;
  commencementDate: string;
  leasePeriod: number;
  internalRateOfReturn: number;
  paymentCycle: string;
}

interface AS19Classification {
  leaseName: string;
  conditionI: string;
  conditionII: string;
  conditionIII: string;
  conditionIV: string;
  conditionV: string;
}

const AS19_CONDITIONS = [
  {
    id: "i",
    description: "The lease transfers ownership of the asset to the lessee by the end of the lease term."
  },
  {
    id: "ii", 
    description: "The lessee has the option to purchase the asset at a price which is expected to be sufficiently lower than the fair value at the date the option becomes exercisable such that, at the inception of the lease, it is reasonably certain that the option will be exercised."
  },
  {
    id: "iii",
    description: "The lease term is for the major part of the economic life of the asset even if title is not transferred."
  },
  {
    id: "iv",
    description: "At the inception of the lease the present value of the minimum lease payments amounts to at least substantially all of the fair value of the leased asset."
  },
  {
    id: "v",
    description: "The leased asset is of a specialised nature such that only the lessee can use it without major modifications being made."
  }
];

export default function LeaseAssessment({ onBack, setActiveSection }: LeaseAssessmentProps) {
  const [leases, setLeases] = useState<LeaseDetails[]>([]);
  const [as19Classifications, setAs19Classifications] = useState<AS19Classification[]>([]);
  const [leasePaymentDetailsById, setLeasePaymentDetailsById] = useState<Record<string, any[]>>({});
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [inferenceQuestion, setInferenceQuestion] = useState<any>({ textResponse: null, documents: [] });
  const [inferenceSaveStatus, setInferenceSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [inferenceSaveError, setInferenceSaveError] = useState<string | null>(null);
  const [globalSaveStatus, setGlobalSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Generate question ID for a section
  const getSectionQuestionId = (sectionName: string): string => {
    const normalizedSection = sectionName
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .toUpperCase();
    return `LEASE_ASSESSMENT_${normalizedSection}`;
  };

  const addLease = () => {
    const newLeaseId = Date.now().toString();
    const newLease: LeaseDetails = {
      id: newLeaseId,
      name: "",
      leaseType: "",
      commencementDate: "",
      leasePeriod: 0,
      internalRateOfReturn: 0,
      paymentCycle: ""
    };
    setLeases([...leases, newLease]);
    setLeasePaymentDetailsById(prev => ({ ...prev, [newLeaseId]: [] }));
  };

  const removeLease = (id: string) => {
    setLeases(leases.filter(lease => lease.id !== id));
    setLeasePaymentDetailsById(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setAs19Classifications(as19Classifications.filter(classification => 
      !leases.find(lease => lease.id === id)?.name || 
      classification.leaseName !== leases.find(lease => lease.id === id)?.name
    ));
  };

  const updateLease = (id: string, field: keyof LeaseDetails, value: string | number) => {
    const updatedLeases = leases.map(lease => 
      lease.id === id ? { ...lease, [field]: value } : lease
    );
    setLeases(updatedLeases);

    // Update AS19 classifications when lease name or type changes
    if (field === 'name' || field === 'leaseType') {
      const lease = updatedLeases.find(l => l.id === id);
      if (lease) {
        const existingClassification = as19Classifications.find(c => c.leaseName === lease.name);
        if (existingClassification) {
          // Update existing classification
          const updatedClassifications = as19Classifications.map(c => 
            c.leaseName === lease.name ? {
              ...c,
              leaseName: lease.name,
              conditionI: lease.leaseType === 'Operating' ? 'No' : c.conditionI,
              conditionII: lease.leaseType === 'Operating' ? 'No' : c.conditionII,
              conditionIII: lease.leaseType === 'Operating' ? 'No' : c.conditionIII,
              conditionIV: lease.leaseType === 'Operating' ? 'No' : c.conditionIV,
              conditionV: lease.leaseType === 'Operating' ? 'No' : c.conditionV
            } : c
          );
          setAs19Classifications(updatedClassifications);
        } else if (lease.name) {
          // Add new classification
          const newClassification: AS19Classification = {
            leaseName: lease.name,
            conditionI: lease.leaseType === 'Operating' ? 'No' : 'No',
            conditionII: lease.leaseType === 'Operating' ? 'No' : 'No',
            conditionIII: lease.leaseType === 'Operating' ? 'No' : 'No',
            conditionIV: lease.leaseType === 'Operating' ? 'No' : 'No',
            conditionV: lease.leaseType === 'Operating' ? 'No' : 'No'
          };
          setAs19Classifications([...as19Classifications, newClassification]);
        }
      }
    }
  };

  const updateAS19Classification = (leaseName: string, condition: string, value: string) => {
    const updatedClassifications = as19Classifications.map(c => 
      c.leaseName === leaseName ? { ...c, [condition]: value } : c
    );
    setAs19Classifications(updatedClassifications);
  };

  // Helper function to format date to dd/mm/yyyy
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Helper function to parse date from dd/mm/yyyy to YYYY-MM-DD
  const parseDate = (dateString: string) => {
    if (!dateString) return "";
    // Format: dd/mm/yyyy
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parts[0].padStart(2, '0');
      const month = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    return "";
  };

  // Load data from SharePoint
  useEffect(() => {
    const loadData = async () => {
      try {
        const fileName = 'Execution_LEASE_LeaseAssessment.json';
        
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson(fileName);

          if (result?.success && result?.data?.leases) {
            const leasesData = result.data.leases;
            
            // Convert loaded leases to LeaseDetails format
            const loadedPaymentDetailsById: Record<string, any[]> = {};
            const loadedLeases: LeaseDetails[] = leasesData.map((lease: any, index: number) => {
              const leaseId = `lease_${Date.now()}_${index}`;
              loadedPaymentDetailsById[leaseId] = Array.isArray(lease.payment_details) ? lease.payment_details : [];
              return {
                id: leaseId,
                name: lease.lease_name || "",
                leaseType: lease.lease_type || "",
                commencementDate: parseDate(lease.commencement_date || ""),
                leasePeriod: lease.lease_period || 0,
                internalRateOfReturn: lease.int_ror || 0,
                paymentCycle: lease.payment_cycle || ""
              };
            });

            setLeases(loadedLeases);
            setLeasePaymentDetailsById(loadedPaymentDetailsById);

            // Load AS19 classifications
            const loadedClassifications: AS19Classification[] = leasesData
              .filter((lease: any) => lease.lease_name)
              .map((lease: any) => ({
                leaseName: lease.lease_name,
                conditionI: lease.conditions?.c_i || "No",
                conditionII: lease.conditions?.c_ii || "No",
                conditionIII: lease.conditions?.c_iii || "No",
                conditionIV: lease.conditions?.c_iv || "No",
                conditionV: lease.conditions?.c_v || "No"
              }));

            setAs19Classifications(loadedClassifications);

            console.log('[LeaseAssessment] Loaded data from SharePoint:', {
              leasesCount: loadedLeases.length,
              classificationsCount: loadedClassifications.length
            });
          } else {
            console.log('[LeaseAssessment] No data found in SharePoint (this is normal for first-time use)');
          }
        } else {
          console.warn('[LeaseAssessment] sharePointAPI.downloadClientJson is not available');
        }
      } catch (error) {
        console.error('[LeaseAssessment] Failed to load data from SharePoint:', error);
      }
    };

    loadData();
  }, []);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load inference question from Execution_LL_Responses.json
  useEffect(() => {
    const loadInferenceQuestion = async () => {
      try {
        const result = await (window as any).sharePointAPI?.downloadClientJson?.("Execution_LL_Responses.json");
        if (result?.success && result?.data?.substantiveProcedures) {
          const inferenceId = "LEASE-ASSESSMENT-INFERENCE";
          const inferenceData = result.data.substantiveProcedures[inferenceId];
          if (inferenceData) {
            setInferenceQuestion({
              textResponse: inferenceData.textResponse || null,
              documents: (inferenceData.documents || []).map((docName: string) => ({
                id: `doc_${docName}`,
                type: 'linked',
                name: docName,
              })),
            });
          }
        }
      } catch (error) {
        console.warn("Failed to load inference question:", error);
      }
    };
    loadInferenceQuestion();
  }, []);

  // Check if JSON can be generated
  // For this procedure, we save whatever is entered as long as at least one lease row exists.
  const canGenerateJSON = () => {
    return leases.length > 0;
  };

  // Generate JSON structure
  const generateJSON = () => {
    const leasesData = leases.map(lease => {
      const classification = as19Classifications.find(c => c.leaseName === lease.name);
      
      return {
        lease_name: lease.name || "",
        lease_type: lease.leaseType || "",
        commencement_date: lease.commencementDate ? formatDate(lease.commencementDate) : "",
        lease_period: lease.leasePeriod || 0,
        int_ror: lease.internalRateOfReturn || 0,
        payment_cycle: lease.paymentCycle || "",
        conditions: {
          c_i: classification?.conditionI || "No",
          c_ii: classification?.conditionII || "No",
          c_iii: classification?.conditionIII || "No",
          c_iv: classification?.conditionIV || "No",
          c_v: classification?.conditionV || "No"
        },
        // Preserve payment details loaded from JSON; this screen should not overwrite them.
        payment_details: leasePaymentDetailsById[lease.id] ?? [
          {
            payment_classification: "",
            payment_type: "",
            payment_amount: 0,
            n_payments: 0,
            int_incr: [],
            incr_per: [],
            month_first_payment: 0
          }
        ]
      };
    });

    return {
      leases: leasesData
    };
  };

  // Upload to SharePoint
  // Save inference question
  const saveInferenceQuestion = async () => {
    if (!companyName) {
      setInferenceSaveError("Company name is required");
      setInferenceSaveStatus('error');
      return;
    }

    setInferenceSaveStatus('saving');
    setInferenceSaveError(null);

    try {
      // Auto-save any pending textbox content
      const documentManagers = document.querySelectorAll('[data-document-manager]');
      for (const manager of Array.from(documentManagers)) {
        const itemId = manager.getAttribute('data-item-id');
        if (itemId) {
          const autoSaveMethod = (window as any)[`autoSaveTextbox_${itemId}`];
          if (autoSaveMethod && typeof autoSaveMethod === 'function') {
            autoSaveMethod();
          }
        }
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      const areaPrefix = "LL";
      const fileName = `Execution_${areaPrefix}_Responses.json`;
      
      let existingData: any = {};
      try {
        const downloadResult = await (window as any).sharePointAPI?.downloadClientJson?.(fileName);
        if (downloadResult?.success && downloadResult?.data) {
          existingData = downloadResult.data;
        }
      } catch (error) {
        console.warn(`Failed to download existing ${fileName}, starting from empty`, error);
      }

      if (!existingData || typeof existingData !== "object" || Array.isArray(existingData)) {
        existingData = {};
      }

      if (!existingData.substantiveProcedures) {
        existingData.substantiveProcedures = {};
      }

      // Save inference question - query db.json for section field like CustomProcedure
      const inferenceId = "LEASE-ASSESSMENT-INFERENCE";
      const sectionToFind = `Execution_${areaPrefix}_${inferenceId}`;
      let inferenceDocumentNames: string[] = [];
      try {
        if ((window as any).sharePointAPI?.loadDocumentListBySection) {
          const dbResult = await (window as any).sharePointAPI.loadDocumentListBySection(sectionToFind);
          if (dbResult?.success && dbResult?.data && Array.isArray(dbResult.data)) {
            inferenceDocumentNames = dbResult.data
              .map((file: any) => file.name || '')
              .filter((name: string) => name && name.trim().length > 0);
          }
        }
      } catch (error) {
        console.warn(`Failed to query db.json for section ${sectionToFind}:`, error);
      }

      const inferenceTextboxFromLocalStorage = localStorage.getItem(`textbox_${inferenceId}`);
      const hasInferenceTextboxContent = inferenceTextboxFromLocalStorage && inferenceTextboxFromLocalStorage.trim().length > 0;
      
      let inferenceTextResponseData = null;
      if (hasInferenceTextboxContent) {
        inferenceTextResponseData = {
          html: inferenceTextboxFromLocalStorage,
          plainText: inferenceTextboxFromLocalStorage.replace(/<[^>]*>/g, '')
        };
      } else if (inferenceQuestion.textResponse) {
        inferenceTextResponseData = inferenceQuestion.textResponse;
      }

      existingData.substantiveProcedures[inferenceId] = {
        selectedOption: "",
        textResponse: inferenceTextResponseData,
        documents: inferenceDocumentNames,
        table: null,
        lastUpdated: new Date().toISOString(),
        companyName,
      };

      const jsonString = JSON.stringify(existingData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }

      const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
        fileContent: base64Content,
        fileName,
        referenceValue: `Execution ${areaPrefix} Substantive Responses`,
        folderName: "juggernaut",
        fyYear: fyYear,
        sectionList: [`Execution_${areaPrefix}_Responses`],
      });

      if (!uploadResult || !uploadResult.success) {
        throw new Error(uploadResult?.error || "Failed to save inference question");
      }

      setInferenceSaveStatus('saved');
      setTimeout(() => setInferenceSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to save inference question:", error);
      setInferenceSaveError(error instanceof Error ? error.message : "Unknown error");
      setInferenceSaveStatus('error');
    }
  };

  const uploadToSharePoint = async (options?: { suppressAlerts?: boolean }) => {
    const suppressAlerts = options?.suppressAlerts ?? false;
    try {
      const jsonData = generateJSON();
      const jsonString = JSON.stringify(jsonData, null, 2);
      
      // Convert JSON string to base64
      const base64Content = btoa(jsonString);

      const fyYear = getFY_YEAR();
      if (!fyYear) {
        throw new Error("FY_YEAR is not configured. Please ensure you have selected an engagement.");
      }
      
      console.log('Uploading lease assessment JSON to SharePoint:', {
        fileName: 'Execution_LEASE_LeaseAssessment.json',
        folderName: 'juggernaut',
        fyYear
      });
      
      // Call SharePoint API to upload file
      if (window.sharePointAPI?.uploadFile) {
        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: 'Execution_LEASE_LeaseAssessment.json',
          referenceValue: '', // Empty as requested
          folderName: 'juggernaut',
          fyYear,
          sectionList: ['Execution_LEASE_LeaseAssessment']
        });

        console.log('SharePoint upload result:', result);

        if (result.success) {
          if (!suppressAlerts) {
            alert(` Successfully uploaded to SharePoint!\nURL: ${result.data?.webUrl}`);
          }
        } else {
          if (!suppressAlerts) {
            alert(` Upload failed: ${result.error || 'Unknown error occurred'}`);
          }
        }
      } else {
        throw new Error('SharePoint API not available');
      }
      
    } catch (error) {
      console.error('Error uploading to SharePoint:', error);
      if (!suppressAlerts) {
        alert(`Error uploading to SharePoint: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
      }
    }
  };

  const handleGlobalSave = async () => {
    if (!canGenerateJSON() || !companyName || inferenceSaveStatus === 'saving' || globalSaveStatus === 'saving') {
      return;
    }

    setGlobalSaveStatus('saving');
    try {
      await uploadToSharePoint({ suppressAlerts: true });
      await saveInferenceQuestion();
      setGlobalSaveStatus('saved');
      setTimeout(() => setGlobalSaveStatus('idle'), 3000);
    } catch (error) {
      console.error("Failed to perform global save:", error);
      setGlobalSaveStatus('error');
      setTimeout(() => setGlobalSaveStatus('idle'), 3000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Lease Assessment Procedures</h2>
          <p className="text-gray-400">Assessment procedures for lease liabilities</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleGlobalSave}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={
              !canGenerateJSON() ||
              inferenceSaveStatus === 'saving' ||
              !companyName ||
              globalSaveStatus === 'saving'
            }
          >
            {globalSaveStatus === 'saving' ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save
              </>
            )}
          </Button>
          <Button variant="outline" onClick={() => setActiveSection?.("ll-as-substantive")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      {/* Lease Details Section */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              <FileCheck2 className="h-5 w-5 text-blue-500" />
              Lease Details
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("Lease Details"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
              <Button onClick={addLease} size="sm" className="bg-blue-600 hover:bg-blue-700">
                <Plus className="h-4 w-4 mr-2" />
                Add Lease
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {leases.length === 0 ? (
            <div className="text-center py-8 text-white/60">
              <p>No leases added yet. Click "Add Lease" to get started.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {leases.map((lease, index) => (
                <div key={lease.id} className="border border-white/10 rounded-lg p-4 bg-white/5">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-white font-medium">Lease #{index + 1}</h4>
                    <Button 
                      onClick={() => removeLease(lease.id)} 
                      size="sm" 
                      variant="outline"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Name of Lease</Label>
                      <Input
                        value={lease.name}
                        onChange={(e) => updateLease(lease.id, 'name', e.target.value)}
                        placeholder="Enter lease name"
                        className="border-white/10 bg-black/40 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Lease Type</Label>
                      <Select value={lease.leaseType} onValueChange={(value) => updateLease(lease.id, 'leaseType', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Operating">Operating</SelectItem>
                          <SelectItem value="Finance">Finance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Commencement Date</Label>
                      <Input
                        type="date"
                        value={lease.commencementDate}
                        onChange={(e) => updateLease(lease.id, 'commencementDate', e.target.value)}
                        className="border-white/10 bg-black/40 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Lease Period (months)</Label>
                      <Input
                        type="number"
                        value={lease.leasePeriod}
                        onChange={(e) => updateLease(lease.id, 'leasePeriod', parseInt(e.target.value) || 0)}
                        placeholder="Enter months"
                        className="border-white/10 bg-black/40 text-white"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Internal Rate of Return (%)</Label>
                      <Input
                        type="number"
                        value={lease.internalRateOfReturn}
                        onChange={(e) => updateLease(lease.id, 'internalRateOfReturn', parseFloat(e.target.value) || 0)}
                        placeholder="Enter %"
                        disabled={lease.leaseType === 'Operating'}
                        className={`border-white/10 bg-black/40 text-white ${lease.leaseType === 'Operating' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-white text-sm">Payment Cycle</Label>
                      <Select value={lease.paymentCycle} onValueChange={(value) => updateLease(lease.id, 'paymentCycle', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select cycle" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                          <SelectItem value="Annually">Annually</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* AS 19 Classification Section */}
      {leases.length > 0 && (
        <Card className="border-white/10 bg-black/40">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-white">
                AS 19 Classification Assessment
              </CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setReviewCommentQuestionId(getSectionQuestionId("AS 19 Classification Assessment"))}
                className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
                title="Review Comment"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-white/70 text-sm">
              Document our considerations for the AS 19 assessment of lease classification as finance lease based on below conditions
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* AS 19 Conditions Table */}
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left text-white font-medium py-3 px-4">Sl. No.</th>
                    <th className="text-left text-white font-medium py-3 px-4">Condition as per AS 19</th>
                  </tr>
                </thead>
                <tbody>
                  {AS19_CONDITIONS.map((condition, index) => (
                    <tr key={condition.id} className="border-b border-white/5">
                      <td className="text-white/80 py-3 px-4 w-16">({condition.id})</td>
                      <td className="text-white/80 py-3 px-4">{condition.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Classification Assessment Table */}
            <div className="space-y-4">
              <h4 className="text-white font-medium">Lease Classification Assessment</h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-white/10">
                  <thead>
                    <tr className="bg-white/5">
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-left">Lease Name</th>
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-center">Condition (i)</th>
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-center">Condition (ii)</th>
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-center">Condition (iii)</th>
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-center">Condition (iv)</th>
                      <th className="border border-white/10 text-white font-medium py-3 px-4 text-center">Condition (v)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leases.filter(lease => lease.name).map((lease) => {
                      const classification = as19Classifications.find(c => c.leaseName === lease.name);
                      return (
                        <tr key={lease.id}>
                          <td className="border border-white/10 text-white py-3 px-4">{lease.name}</td>
                          {['conditionI', 'conditionII', 'conditionIII', 'conditionIV', 'conditionV'].map((condition, index) => (
                            <td key={condition} className="border border-white/10 py-3 px-4">
                              <Select
                                value={classification?.[condition as keyof AS19Classification] || 'No'}
                                onValueChange={(value) => updateAS19Classification(lease.name, condition, value)}
                                disabled={lease.leaseType === 'Operating'}
                              >
                                <SelectTrigger className={lease.leaseType === 'Operating' ? 'w-full opacity-50 cursor-not-allowed' : 'w-full'}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Yes">Yes</SelectItem>
                                  <SelectItem value="No">No</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inference and Conclusion Question */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Document the inference and conclusion from the above procedure performed.</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <DocumentManager
            sectionKey="lease-liability"
            itemId="LEASE-ASSESSMENT-INFERENCE"
            itemQuestion="Document the inference and conclusion from the above procedure performed."
            itemTextResponse={inferenceQuestion.textResponse}
            itemDocuments={inferenceQuestion.documents || []}
            onTextResponseChange={(textResponse) => setInferenceQuestion({ ...inferenceQuestion, textResponse })}
            onDocumentsChange={(documents) => setInferenceQuestion({ ...inferenceQuestion, documents })}
            disabled={false}
          />
        </CardContent>
      </Card>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            // Map questionId back to section name for display
            const sectionMap: Record<string, string> = {
              [getSectionQuestionId("Lease Details")]: "Lease Details",
              [getSectionQuestionId("AS 19 Classification Assessment")]: "AS 19 Classification Assessment",
              [getSectionQuestionId("Save to SharePoint")]: "Save to SharePoint",
            };
            return sectionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
